import { type PropsWithChildren, useCallback, useState } from 'react';

import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { type DiagramArtifactPromptDiagramArtifactPromptType } from '@/types/schema';

import { TEMPLATE_DETAILS } from '../DiagramBuilder.constants';
import {
  ArtifactStudioContext,
  type NewDiagramPrompt,
} from './artifactStudio.context';

// The editor trowser target is the saved artifact ID. The first request of a
// new diagram is transient trowser data, so a reload does not send it again.
declare module '@/modules/navigation/trowsers/trowser.types' {
  interface TrowserDataMap {
    [TrowserKey.Artifact]: { newDiagram?: NewDiagramPrompt };
  }
}

interface ArtifactStudioProviderProps {
  householdId: string;
}

function useContextValue({
  householdId,
}: ArtifactStudioProviderProps): ArtifactStudioContext {
  const trowser = useTrowser(TrowserKey.Artifact);

  const [prompt, setPrompt] = useState('');
  const [selectedTemplate, setSelectedTemplate] =
    useState<DiagramArtifactPromptDiagramArtifactPromptType | null>(null);
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [isValidatingReferenceImage, setIsValidatingReferenceImage] =
    useState(false);
  const [referenceImageError, setReferenceImageError] = useState('');
  // A reference image needs a written request to go with it.
  const canCreateArtifact = Boolean(prompt.trim() || selectedTemplate);

  // The editor calls this from an effect, so keep it stable between renders.
  const { open: openTrowser, data: trowserData } = trowser;
  const showCreatedArtifact = useCallback(
    (artifactID: string) =>
      // Keep the first request so the editor that sent it stays mounted.
      openTrowser({ target: artifactID, data: trowserData }),
    [openTrowser, trowserData]
  );

  function createArtifact() {
    if (!canCreateArtifact || isValidatingReferenceImage) return;
    trowser.open({
      data: {
        newDiagram: {
          message: prompt.trim(),
          withPromptTemplate: selectedTemplate
            ? TEMPLATE_DETAILS[selectedTemplate].promptTemplate
            : undefined,
          referenceImage: referenceImage ?? undefined,
        },
      },
    });
    setPrompt('');
    setSelectedTemplate(null);
    setReferenceImage(null);
  }

  return {
    householdId,
    prompt,
    setPrompt,
    selectedTemplate,
    setSelectedTemplate,
    referenceImage,
    setReferenceImage,
    isValidatingReferenceImage,
    setIsValidatingReferenceImage,
    referenceImageError,
    setReferenceImageError,
    canCreateArtifact,
    createArtifact,
    isEditorOpen: trowser.isOpen,
    editorArtifactID: trowser.target ?? null,
    newDiagram: trowser.data?.newDiagram,
    openArtifact: (artifactID) => trowser.open({ target: artifactID }),
    showCreatedArtifact,
    closeEditor: trowser.close,
  };
}

/** Provides the Artifact Studio page state. See ArtifactStudioContext. */
export function ArtifactStudioProvider({
  children,
  ...props
}: PropsWithChildren<ArtifactStudioProviderProps>) {
  const value = useContextValue(props);
  return (
    <ArtifactStudioContext.Provider value={value}>
      {children}
    </ArtifactStudioContext.Provider>
  );
}
